import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createDealerBankAccount,
    deleteDealerBankAccount,
    fetchDealerBankAccounts,
    updateDealerBankAccount,
} from '../services/dealerBankAccounts';
import { dealerKeys } from './useDealersQuery';
import type { CreateDealerBankAccountPayload, UpdateDealerBankAccountPayload } from '../types/dealers.types';

export const useDealerBankAccounts = (dealerId: string) => {
    return useQuery({
        queryKey: dealerKeys.bankAccounts(dealerId),
        queryFn: () => fetchDealerBankAccounts(dealerId),
        enabled: !!dealerId,
    });
};

export const useDealerBankAccountMutations = (dealerId: string) => {
    const queryClient = useQueryClient();

    const invalidate = async () => {
        await queryClient.invalidateQueries({ queryKey: dealerKeys.bankAccounts(dealerId) });
    };

    const createMutation = useMutation({
        mutationFn: (payload: CreateDealerBankAccountPayload) => createDealerBankAccount(dealerId, payload),
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: ({ bankAccountId, payload }: { bankAccountId: string; payload: UpdateDealerBankAccountPayload }) =>
            updateDealerBankAccount(dealerId, bankAccountId, payload),
        onSuccess: invalidate,
    });

    const deleteMutation = useMutation({
        mutationFn: (bankAccountId: string) => deleteDealerBankAccount(dealerId, bankAccountId),
        onSuccess: invalidate,
    });

    return {
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
